import { useMemo, useState, type MouseEvent as ReactMouseEvent } from 'react';
import { motion } from 'motion/react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceDot,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { AnalogPricePoint, AnalogSignificantMove } from '@/mocks/stockAnalogMock';
import {
  buildWholeDollarYAxisTicks,
  formatPriceAxisTick,
} from '@/utils/historicalChartWholeDollarTicks';
import { formatPct, pctChipClass } from '@/components/AnalogLensAccordion';

type Props = {
  path: AnalogPricePoint[];
  anchorDate: string;
  anchorClose: number;
  className?: string;
  significantMoves?: AnalogSignificantMove[];
  /**
   * Shared session-offset domain so stacked charts place the analog-day
   * reference line at the same horizontal position.
   */
  sessionDomain?: [number, number];
  /** Hide x-axis labels — use on upper charts in a synced stack. */
  hideXAxis?: boolean;
};

type ChartRow = {
  t: number;
  date: string;
  price: number | null;
};

const LINE_COLOR = '#374151';
const Y_AXIS_WIDTH = 52;

function addTradingDays(isoDate: string, days: number): string {
  // Map session offsets to weekdays only — calendar addDays invented Sundays
  // (e.g. 2024-06-24 + (-1) → 2024-06-23).
  const d = new Date(`${isoDate}T12:00:00`);
  const step = days >= 0 ? 1 : -1;
  let remaining = Math.abs(days);
  while (remaining > 0) {
    d.setDate(d.getDate() + step);
    const wd = d.getDay(); // 0=Sun … 6=Sat
    if (wd !== 0 && wd !== 6) remaining -= 1;
  }
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatDayLabel(dateStr: string): string {
  try {
    const [y, m, d] = dateStr.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

function formatMoveDate(dateStr: string): string {
  try {
    const [y, m, d] = dateStr.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

/** Evenly spaced session offsets, always including t=0 (analog day). */
function buildSessionTicks(tMin: number, tMax: number): number[] {
  if (tMax <= tMin) return [tMin];
  const ticks = new Set<number>([tMin, 0, tMax]);
  const span = tMax - tMin;
  const step = Math.max(1, Math.round(span / 4));
  for (let t = tMin + step; t < tMax; t += step) {
    ticks.add(t);
  }
  return [...ticks].filter((t) => t >= tMin && t <= tMax).sort((a, b) => a - b);
}

function resolveSessionDomain(
  path: AnalogPricePoint[],
  override?: [number, number],
): [number, number] {
  if (override) return override;
  const ts = path.map((p) => p.t).filter((t) => Number.isFinite(t));
  if (!ts.length) return [-10, 40];
  return [Math.min(...ts), Math.max(...ts)];
}

function ChartTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{ payload: ChartRow }>;
}) {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  if (row.price == null || !Number.isFinite(row.price)) return null;
  try {
    const [y, m, d] = row.date.split('-').map(Number);
    const label = new Date(y, m - 1, d).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
    return (
      <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs shadow-sm">
        <p className="text-gray-500 mb-0.5">{label}</p>
        <p className="font-medium text-gray-900 tabular-nums">${row.price.toFixed(2)}</p>
      </div>
    );
  } catch {
    return null;
  }
}

function SignificantMoveDot({
  cx,
  cy,
  move,
  isSelected,
  onSelect,
}: {
  cx?: number;
  cy?: number;
  move: AnalogSignificantMove;
  isSelected: boolean;
  onSelect: (date: string) => void;
}) {
  const [isHovered, setIsHovered] = useState(false);

  if (cx == null || cy == null) return null;

  const isUp = move.percentChange >= 0;
  const fill = isUp ? '#10b981' : '#f43f5e';
  const baseRadius = 5;
  const hoverRadius = 8;
  const dotRadius = isSelected ? baseRadius : isHovered ? hoverRadius : baseRadius;

  const handleClick = (event: ReactMouseEvent) => {
    event.stopPropagation();
    onSelect(move.date);
  };

  return (
    <g
      data-chart-interactive-marker="true"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{ cursor: 'pointer' }}
    >
      <circle
        cx={cx}
        cy={cy}
        r={16}
        fill="transparent"
        strokeWidth={0}
        pointerEvents="all"
        onClick={handleClick}
        onMouseDown={(event) => event.stopPropagation()}
      />
      {isSelected ? (
        <motion.circle
          cx={cx}
          cy={cy}
          r={dotRadius}
          fill="none"
          stroke={fill}
          strokeWidth={1.5}
          strokeOpacity={0}
          animate={{
            r: [dotRadius, 16],
            strokeOpacity: [0.4, 0],
          }}
          transition={{
            duration: 1.8,
            repeat: Infinity,
            ease: 'easeOut',
            repeatDelay: 0.2,
          }}
          style={{ pointerEvents: 'none' }}
        />
      ) : null}
      <circle
        cx={cx}
        cy={cy}
        r={dotRadius}
        fill={fill}
        strokeWidth={0}
        pointerEvents="none"
        style={{ transition: 'r 0.2s ease-in-out' }}
      />
    </g>
  );
}

function SignificantMoveExplanation({
  move,
  onClose,
}: {
  move: AnalogSignificantMove;
  onClose: () => void;
}) {
  const isUp = move.percentChange >= 0;
  const accentBorder = isUp ? 'border-emerald-100' : 'border-rose-100';
  const accentBg = isUp ? 'bg-emerald-50/60' : 'bg-rose-50/60';

  return (
    <div className={`mt-3 rounded-xl border ${accentBorder} ${accentBg} p-4`}>
      <div className="mb-2 flex items-start justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold text-gray-900">
            {formatMoveDate(move.date)}
          </span>
          <span className={`text-sm font-medium ${pctChipClass(move.percentChange)}`}>
            {formatPct(move.percentChange)}
          </span>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="shrink-0 text-xs text-gray-500 hover:text-gray-800"
        >
          Close
        </button>
      </div>
      <p className="text-sm leading-relaxed text-gray-700">{move.summary}</p>
      {move.primaryCatalyst ? (
        <p className="mt-3 text-xs leading-relaxed text-gray-600">
          <span className="font-medium text-gray-800">Primary catalyst: </span>
          {move.primaryCatalyst}
        </p>
      ) : null}
      {move.sectorContext ? (
        <p className="mt-2 text-xs leading-relaxed text-gray-500">{move.sectorContext}</p>
      ) : null}
      {move.secondaryCatalysts && move.secondaryCatalysts.length > 0 ? (
        <ul className="mt-3 list-disc space-y-1 pl-4 text-xs leading-relaxed text-gray-600">
          {move.secondaryCatalysts.slice(0, 4).map((item) => (
            <li key={item.slice(0, 48)}>{item}</li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/** Build a shared [tMin, tMax] across several analog paths for stacked alignment. */
export function sharedSessionDomain(
  paths: AnalogPricePoint[][],
  fallback: [number, number] = [-10, 40],
): [number, number] {
  const ts: number[] = [];
  for (const path of paths) {
    for (const p of path) {
      if (Number.isFinite(p.t)) ts.push(p.t);
    }
  }
  if (!ts.length) return fallback;
  return [Math.min(...ts), Math.max(...ts)];
}

export function AnalogForwardMiniChart({
  path,
  anchorDate,
  anchorClose,
  className = '',
  significantMoves = [],
  sessionDomain,
  hideXAxis = false,
}: Props) {
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  const domain = useMemo(
    () => resolveSessionDomain(path, sessionDomain),
    [path, sessionDomain],
  );

  const chartData = useMemo<ChartRow[]>(() => {
    // Only plot real observations. Alignment comes from a shared numeric
    // domain on the X axis — do NOT null-pad missing session offsets, or
    // Recharts will break the line into gaps.
    return path
      .filter((p) => Number.isFinite(p.t) && Number.isFinite(p.price))
      .map((p) => ({
        t: p.t,
        date: p.date ?? addTradingDays(anchorDate, p.t),
        price: (p.price / 100) * anchorClose,
      }))
      .sort((a, b) => a.t - b.t);
  }, [path, anchorDate, anchorClose]);

  const xTicks = useMemo(() => buildSessionTicks(domain[0], domain[1]), [domain]);

  const dateByT = useMemo(() => {
    const map = new Map<number, string>();
    for (const row of chartData) map.set(row.t, row.date);
    // Fill tick labels for domain edges / anchor even if this series is shorter.
    for (const t of xTicks) {
      if (!map.has(t)) map.set(t, addTradingDays(anchorDate, t));
    }
    return map;
  }, [chartData, xTicks, anchorDate]);

  const { yAxisDomain, yAxisTicks, yAxisTickStep } = useMemo(() => {
    const prices = chartData
      .map((r) => r.price)
      .filter((p): p is number => p != null && Number.isFinite(p));
    if (!prices.length) {
      const built = buildWholeDollarYAxisTicks(0, anchorClose * 1.2);
      return {
        yAxisDomain: [0, anchorClose * 1.2] as [number, number],
        yAxisTicks: built.ticks,
        yAxisTickStep: built.step,
      };
    }
    const dataMin = Math.min(...prices);
    const dataMax = Math.max(...prices);
    const span = dataMax - dataMin;
    const pad = span > 0 ? span * 0.08 : anchorClose * 0.05;
    const domainMin = Math.max(0, dataMin - pad);
    const domainMax = Math.max(dataMax + pad, domainMin + 0.01);
    const built = buildWholeDollarYAxisTicks(domainMin, domainMax);
    return {
      yAxisDomain: [domainMin, domainMax] as [number, number],
      yAxisTicks: built.ticks,
      yAxisTickStep: built.step,
    };
  }, [chartData, anchorClose]);

  const visibleMoves = useMemo(
    () =>
      significantMoves.filter((move) =>
        chartData.some((row) => row.date === move.date && row.price != null),
      ),
    [significantMoves, chartData],
  );

  const selectedMove = useMemo(
    () => visibleMoves.find((move) => move.date === selectedDate) ?? null,
    [visibleMoves, selectedDate],
  );

  const handleSelectMove = (date: string) => {
    setSelectedDate((current) => (current === date ? null : date));
  };

  const formatTick = (t: number) => {
    const date = dateByT.get(t);
    if (!date) return '';
    return formatDayLabel(date);
  };

  return (
    <div className={className}>
      <ResponsiveContainer width="100%" height={hideXAxis ? 168 : 200}>
        <LineChart
          data={chartData}
          margin={{ top: 8, right: 12, left: 4, bottom: hideXAxis ? 0 : 4 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            stroke="#f3f4f6"
            vertical={false}
            syncWithTicks
          />
          <XAxis
            dataKey="t"
            type="number"
            domain={domain}
            ticks={xTicks}
            tickFormatter={formatTick}
            tick={hideXAxis ? false : { fill: '#9ca3af', fontSize: 11 }}
            tickLine={false}
            axisLine={hideXAxis ? false : { stroke: '#e5e7eb' }}
            dy={hideXAxis ? 0 : 8}
            height={hideXAxis ? 0 : 28}
            allowDecimals={false}
            allowDataOverflow
          />
          <YAxis
            domain={yAxisDomain}
            ticks={yAxisTicks}
            tick={{ fill: '#9ca3af', fontSize: 12 }}
            tickLine={false}
            axisLine={false}
            tickFormatter={(v) => formatPriceAxisTick(v, yAxisTickStep)}
            width={Y_AXIS_WIDTH}
          />
          <Tooltip
            content={<ChartTooltip />}
            cursor={{ stroke: '#d1d5db', strokeWidth: 1 }}
          />
          <ReferenceLine
            x={0}
            stroke="#9ca3af"
            strokeDasharray="4 4"
            strokeWidth={1}
          />
          {visibleMoves.map((move) => {
            const row = chartData.find((r) => r.date === move.date);
            if (!row || row.price == null) return null;
            return (
              <ReferenceDot
                key={move.date}
                x={row.t}
                y={row.price}
                shape={(props) => (
                  <SignificantMoveDot
                    cx={props.cx}
                    cy={props.cy}
                    move={move}
                    isSelected={selectedDate === move.date}
                    onSelect={handleSelectMove}
                  />
                )}
              />
            );
          })}
          <Line
            type="monotone"
            dataKey="price"
            stroke={LINE_COLOR}
            strokeWidth={2}
            dot={false}
            connectNulls
            activeDot={{ r: 3, fill: LINE_COLOR, strokeWidth: 0 }}
          />
        </LineChart>
      </ResponsiveContainer>
      {selectedMove ? (
        <SignificantMoveExplanation
          move={selectedMove}
          onClose={() => setSelectedDate(null)}
        />
      ) : null}
    </div>
  );
}
